/// <amd-module name="VSS/Features/MarkdownEditor/Formatting/Common" />
import { IMarkdownFormattingArgs, IMarkdownFormattingResult, IMarkdownTextSource } from "./Contracts";

export const newLineChar = "\n";

export interface ITextRange {
    start: number;
    end: number;
}

export namespace MarkdownSigns {
    export const Bold = "**";
    export const Italic = "_";
    export const Header1 = "# ";
    export const Header2 = "## ";
    export const Header3 = "### ";
    export const BulletedList = "- ";
    export const NumberedList = "1. ";
    export const TaskList = "- [ ] ";
    export const Code = "`";
    export const CodeBlock = newLineChar + "```" + newLineChar;
}

export function insertText(formattingArgs: IMarkdownFormattingArgs, insertText?: string): IMarkdownFormattingResult {
    const selectionStart = formattingArgs.selectionStart || 0;
    const selectionEnd = formattingArgs.selectionEnd || 0;
    insertText = insertText || formattingArgs.text.substring(selectionStart, selectionEnd);

    return {
        finalSelectionEnd: selectionStart + insertText.length,
        finalSelectionStart: selectionStart + insertText.length,
        finalText: formattingArgs.text.substring(0, selectionStart) + insertText + formattingArgs.text.substring(selectionEnd),
        insertSelectionEnd: selectionEnd,
        insertSelectionStart: selectionStart,
        insertText
    };
}

export function insertSignAtLineStart(
    formattingArgs: IMarkdownFormattingArgs,
    sign: string,
    signsToReplace: string[] = []
): IMarkdownFormattingResult {
    const { text = "" } = formattingArgs;
    let selectionStart = Math.min(formattingArgs.selectionStart || 0, text.length);
    const selectionEnd = Math.min(formattingArgs.selectionEnd || 0, text.length);

    // Move the cursor to the start of the current line for things like lists
    selectionStart = text.substring(0, selectionStart).lastIndexOf(newLineChar) + 1;
    const selectedText = text.substring(selectionStart, selectionEnd);
    const selectedLines = selectedText.split(newLineChar).map(line => {
        // This condition is to avoid adding markdown sign if no character is selected in last line
        if (!line && selectionStart !== selectionEnd) {
            return line;
        }

        const signToReplace = signsToReplace.filter(s => line.indexOf(s) === 0)[0];
        if (line.indexOf(sign) === 0) {
            // Line starts with sign so we should remove it (toggling)
            return line.substring(sign.length);
        } else if (signToReplace) {
            // Line starts with one of the signs to replace, so make the replacement
            return sign + line.substring(signToReplace.length);
        } else {
            // Just add sign to the beginning of the line
            return sign + line;
        }
    });

    const firstPart = text.substring(0, selectionStart);
    const endPart = text.substring(selectionEnd);

    const replacementText = selectedLines.join(newLineChar);
    const cursorPosition = firstPart.length + replacementText.length;

    return {
        insertSelectionEnd: selectionEnd,
        insertSelectionStart: selectionStart,
        insertText: replacementText,
        finalSelectionEnd: cursorPosition,
        finalSelectionStart: cursorPosition,
        finalText: firstPart + replacementText + endPart
    };
}

export function wrapWithSign(formatArgs: IMarkdownFormattingArgs, sign: string, endSign = sign): IMarkdownFormattingResult {
    const { text = "" } = formatArgs;
    let selectionStart = Math.min(formatArgs.selectionStart || 0, text.length);
    let selectionEnd = Math.min(formatArgs.selectionEnd || 0, text.length);

    let cursorPosition = selectionStart;
    const wrapperStartLength = sign.length;
    const wrapperEndLength = endSign.length;
    const hasSelection = selectionEnd > selectionStart;

    if (hasSelection) {
        // If we do have a selection, trim beginning/trailing whitespace
        const trimmedSelectionRange = getTrimmedSelectionRange(text, selectionStart, selectionEnd);
        selectionStart = trimmedSelectionRange.start;
        selectionEnd = trimmedSelectionRange.end;

        // Expand to grab the wrappers if they already exists
        if (selectionStart - wrapperStartLength >= 0 && text.substr(selectionStart - wrapperStartLength, wrapperStartLength) === sign) {
            selectionStart -= wrapperStartLength;
        }

        if (text.substr(selectionEnd, wrapperEndLength) === endSign) {
            selectionEnd += wrapperEndLength;
        }
    } else {
        // There is no selection but a wrap is requestted. In this case, we try to find the word
        // the cursor is in and wrap it using the specified sign
        const cursorWord = expandCursorToWord(text, selectionStart);
        selectionStart = cursorWord.start;
        selectionEnd = cursorWord.end;
    }

    const firstPart = text.substring(0, selectionStart);
    const middlePart = text.substring(selectionStart, selectionEnd);
    const endPart = text.substring(selectionEnd);

    let replacementText: string;

    // If the specified sign is already present, remove it. Otherwise, add it
    const wrapperTextExists =
        sign === middlePart.substr(0, wrapperStartLength) && endSign === middlePart.substr(middlePart.length - wrapperEndLength);

    if (wrapperTextExists) {
        replacementText = middlePart.substr(wrapperStartLength, middlePart.length - wrapperStartLength - wrapperEndLength);
        cursorPosition -= wrapperStartLength;

        if (hasSelection) {
            selectionEnd -= wrapperStartLength + wrapperEndLength;
        } else {
            selectionStart -= wrapperStartLength;
            selectionEnd -= wrapperEndLength;
        }
    } else {
        replacementText = sign + middlePart + endSign;
        cursorPosition += wrapperStartLength;
        selectionStart += wrapperStartLength;
        selectionEnd += wrapperStartLength;
    }

    if (!hasSelection) {
        selectionStart = selectionEnd = cursorPosition;
    }

    return {
        insertSelectionEnd: firstPart.length + middlePart.length,
        insertSelectionStart: firstPart.length,
        insertText: replacementText,
        finalSelectionEnd: selectionEnd,
        finalSelectionStart: selectionStart,
        finalText: firstPart + replacementText + endPart
    };
}

/**
 * Selects the whole word, if the cursor is in the middle of a word without selection.
 *
 * @param text Text to format.
 * @param cursorPosition Current position of the cursor.
 */
export function expandCursorToWord(text: string, cursorPosition: number): ITextRange {
    const left = text.slice(0, cursorPosition + 1).search(/\S+$/);
    const right = text.slice(cursorPosition).search(/\s/);

    let start: number;
    let end: number;

    if (left < 0) {
        start = cursorPosition;
    } else {
        start = left;
    }

    if (right < 0) {
        end = text.length;
    } else {
        end = right + cursorPosition;
    }

    start = Math.min(Math.max(0, start), text.length);
    end = Math.min(Math.max(0, end), text.length);

    return { start, end };
}

export function getTrimmedSelectionRange(text: string, start: number, end: number): ITextRange {
    while (start < end && /\s/.test(text[start])) {
        start++;
    }

    while (end > start && /\s/.test(text[end - 1])) {
        end--;
    }

    return { end, start };
}

export function applyTextFormatting(
    textSource: IMarkdownTextSource,
    formattingResult: IMarkdownFormattingResult | Promise<IMarkdownFormattingResult | void> | void
): void {
    if (formattingResult) {
        if ((formattingResult as Partial<Promise<IMarkdownFormattingResult | void>>).then) {
            (formattingResult as Promise<IMarkdownFormattingResult | void>).then(
                asyncResult => {
                    if (asyncResult) {
                        textSource.applyFormatting(asyncResult);
                    }
                },
                error => textSource.showError(error)
            );
        } else {
            textSource.applyFormatting(formattingResult as IMarkdownFormattingResult);
        }
    }
}
